import { useEffect, useMemo } from 'react';

import { filter, includes } from 'lodash';

import { LabelsService } from '@/utils/services/LabelsService';

import { ADD_LABEL, REMOVE_LABEL, useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import { useGroupLabels } from '@/utils/hooks/swr/useGroupLabels';
import useLabels from '@/utils/hooks/swr/useLabels';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

import Icon from '@/components/base/Icon';
import Label from '@/components/base/Label';
import { Skeleton } from '@/components/base/Skeleton/Skeleton';
import SelectAlbumTags from '@/components/pages/albums/AlbumFunnelModal/components/SelectAlbumTags';

export const AlbumTagsFilter = () => {
    const { t } = useTranslation('content');
    const { albumInfo, dispatch } = useAlbumContext();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();
    const isGroup = useIsGroup();

    const {
        labels: socialProfileLabels,
        isLoadingInitialData: isLoadingLabelsInitialData,
        requestParams: socialProfileRequestParams,
        setRequestParams: setSocialProfileRequestParams
    } = useLabels({
        activeSPID: activeSocialProfile?.id,
        options: { revalidateOnFocus: false, revalidateOnReconnect: false },
        shouldFetch: !isGroup
    });
    const {
        labels: groupLabels,
        isLoadingInitialData: isLoadingGroupLabelsInitialData,
        requestParams: groupRequestParams,
        setRequestParams: setGroupRequestParams
    } = useGroupLabels({
        activeGroupID: activeGroup?.id,
        options: { revalidateOnFocus: false, revalidateOnReconnect: false },
        shouldFetch: isGroup
    });

    const labels = isGroup ? groupLabels : socialProfileLabels;
    const isLoadingInitialData = isGroup
        ? isLoadingGroupLabelsInitialData
        : isLoadingLabelsInitialData;
    const requestParams = isGroup ? groupRequestParams : socialProfileRequestParams;
    const setRequestParams = isGroup ? setGroupRequestParams : setSocialProfileRequestParams;

    useEffect(() => {
        if (albumInfo.criteria?.album_type) {
            setRequestParams({ media_type: 'stories' });
        }
        if (!albumInfo.criteria?.album_type && requestParams?.media_type) {
            setRequestParams(undefined);
        }
    }, [albumInfo.criteria?.album_type]);

    const debouncedSearch = useMemo(
        () => LabelsService.createDebouncedSearch(setRequestParams),
        [setRequestParams]
    );

    useEffect(() => {
        return () => {
            if (typeof (debouncedSearch as any).cancel === 'function') {
                (debouncedSearch as any).cancel();
            }
        };
    }, [debouncedSearch]);

    function addLabel(item) {
        dispatch({
            type: ADD_LABEL,
            payload: item?.label
        });
    }

    function removeLabel(item) {
        dispatch({
            type: REMOVE_LABEL,
            payload: item?.label
        });
    }

    const selectedLabels = filter(labels, (item) =>
        includes(albumInfo?.criteria?.labels, item?.label)
    );

    const renderField = () => {
        if (isLoadingInitialData) {
            return <Skeleton className="h-12 w-full" type="square" />;
        }

        return (
            <SelectAlbumTags
                placeholder={t('modal.select_labels')}
                idInput="labels-search"
                buttonStyle="whiteWithBorder"
                options={filter(
                    labels,
                    (item) => !albumInfo?.criteria?.labels?.includes(item?.label)
                )}
                selectedItems={selectedLabels}
                // @ts-ignore
                addItem={addLabel}
                // @ts-ignore
                removeItem={removeLabel}
                onSearch={debouncedSearch}
            />
        );
    };

    return (
        <div>
            <Label className="flex items-center space-x-2.5 pb-2">
                <Icon icon="labels" className="text-sm text-icoBlue" />
                <span className="text-icoBlack dark:text-white">{t('modal.labels')}</span>
            </Label>
            {renderField()}
        </div>
    );
};
